import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import { getSchedulingInstanceUsers } from '../../apis/schedulingInstances';
import { NOT_AUTHORIZED_DEFAULT } from '../../const/messageCodes';
import { INITIAL_LIMIT, PAGINATION_LIMIT_MIN } from '../../const/pagination';
import { PageInfo, PageInfoQueryWithSearch } from '../../models/PaginatedResponse';
import { OrgUser } from '../../models/SqspUser';
import { reduxErrorHandler } from '../../utils/reduxError';
import { AppThunk } from '../store';

export type SchedulingInstanceUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
  pageInfo: PageInfo;
} & EntityState<OrgUser>;

const schedulingInstanceUsersAdapter = createEntityAdapter<OrgUser>();

export const schedulingInstanceUsersSlice = createSlice<
  SchedulingInstanceUsersState,
  SliceCaseReducers<SchedulingInstanceUsersState>,
  'schedulingInstanceUsers'
>({
  name: 'schedulingInstanceUsers',
  initialState: schedulingInstanceUsersAdapter.getInitialState({
    isLoading: true,
    error: null,
    pageInfo: {
      total: 0,
      limit: PAGINATION_LIMIT_MIN,
      offset: 0,
    },
  }),
  reducers: {
    schedulingInstanceUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    schedulingInstanceUsersReceived(state, action: PayloadAction<OrgUser[]>) {
      schedulingInstanceUsersAdapter.setAll(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    appendSchedulingInstanceUsers(state, action: PayloadAction<OrgUser[]>) {
      schedulingInstanceUsersAdapter.upsertMany(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    updatePageInfo(state, action: PayloadAction<PageInfo>) {
      state.pageInfo = action.payload;
      state.isLoading = false;
    },
  },
});

export const { reducer: schedulingInstanceUsersReducer } = schedulingInstanceUsersSlice;

export const {
  selectAll: selectAllSchedulingInstanceUsers,
  selectById: selectSchedulingInstanceUserById,
  selectTotal: selectTotalSchedulingInstanceUsers,
} = schedulingInstanceUsersAdapter.getSelectors();

export const {
  schedulingInstanceUsersRequested,
  schedulingInstanceUsersReceived,
  appendSchedulingInstanceUsers,
  errorReceived,
  updatePageInfo,
} = schedulingInstanceUsersSlice.actions;

// Async actions

// Async actions
/**
 * Will fetch scheduling instances and APPEND to the current data store of scheduling instances
 * @param {number} enterpriseId
 * @param {number} instanceId
 * @param {Partial<PageInfo>} options
 * @returns {AppThunk}
 */
export const fetchBackgroundSchedulingInstanceUsers = (
  enterpriseId: number,
  instanceId: number,
  options?: Partial<PageInfo>,
): AppThunk => {
  return async (dispatch) => {
    try {
      // only fetch if offset is provided and is at least greater than INITIAL_LIMIT
      if (options?.offset && options.offset >= INITIAL_LIMIT) {
        const schedulingInstanceUsersResponse = await getSchedulingInstanceUsers(enterpriseId, instanceId, options);

        dispatch(appendSchedulingInstanceUsers(schedulingInstanceUsersResponse.data));
        dispatch(updatePageInfo(schedulingInstanceUsersResponse.pageInfo));

        if (
          options.offset + schedulingInstanceUsersResponse.data.length <
          schedulingInstanceUsersResponse.pageInfo.total
        ) {
          dispatch(
            fetchBackgroundSchedulingInstanceUsers(enterpriseId, instanceId, {
              ...options,
              offset: options.offset + schedulingInstanceUsersResponse.data.length,
            }),
          );
        }
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_DEFAULT);
    }
  };
};

/**
 * Initial fetch for listing scheduling instances
 * @param {number} enterpriseId
 * @param {number} instanceId
 * @param {Partial<PageInfoQueryWithSearch>} options
 * @returns {AppThunk}
 */
export const fetchSchedulingInstanceUsers = (
  enterpriseId: number,
  instanceId: number,
  options?: Partial<PageInfoQueryWithSearch>,
): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(schedulingInstanceUsersRequested(undefined));
      const schedulingInstancesResponse = await getSchedulingInstanceUsers(enterpriseId, instanceId, options);

      dispatch(schedulingInstanceUsersReceived(schedulingInstancesResponse.data));
      dispatch(updatePageInfo(schedulingInstancesResponse.pageInfo));

      if (schedulingInstancesResponse.data.length < schedulingInstancesResponse.pageInfo.total) {
        dispatch(
          fetchBackgroundSchedulingInstanceUsers(enterpriseId, instanceId, {
            ...options,
            offset: schedulingInstancesResponse.data.length,
          }),
        );
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_DEFAULT);
    }
  };
};
